import { z } from 'zod';

/**
 * Documents ("Hujjatlar") and reports ("Hisobotlar") — docs/design/03-modules.md §5.
 * Both sections share one model; permissions come from the monitoring menu of the section
 * (`documents.*` / `reports.*`): view = list/download (+ visibility), upload = add documents and create folders,
 * edit = rename/move/change visibility (owner or tenant admin), delete = delete.
 *
 * Upload flow: `POST /tenants/:t/files?kind=document` (multipart) → `POST /tenants/:t/documents { fileId, … }`.
 */

export const DOCUMENT_SECTIONS = ['documents', 'reports'] as const;
export type DocumentSection = (typeof DOCUMENT_SECTIONS)[number];

/** "Ko'rinishi": Shaxsiy (owner + tenant admins) · Tanlanganlar (owner + admins + shared users) · Ommaviy (everyone with view). */
export const DOCUMENT_VISIBILITIES = ['private', 'selected', 'public'] as const;
export type DocumentVisibility = (typeof DOCUMENT_VISIBILITIES)[number];
export const DOCUMENT_VISIBILITY_LABELS: Record<DocumentVisibility, string> = {
  private: 'Shaxsiy',
  selected: 'Tanlanganlar',
  public: 'Ommaviy',
};

/** "Turi" column; folders are "Jild". */
export const DOCUMENT_TYPES = ['pdf', 'word', 'excel', 'powerpoint', 'image', 'csv', 'other'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];
export const DOCUMENT_TYPE_LABELS: Record<DocumentType | 'folder', string> = {
  folder: 'Jild',
  pdf: 'Pdf',
  word: 'Word',
  excel: 'Excel',
  powerpoint: 'PowerPoint',
  image: 'Rasm',
  csv: 'CSV',
  other: 'Boshqa',
};

export const DOCUMENT_SORTS = [
  'name',
  '-name',
  'createdAt',
  '-createdAt',
  'updatedAt',
  '-updatedAt',
  'size',
  '-size',
  'type',
  '-type',
] as const;
export type DocumentSort = (typeof DOCUMENT_SORTS)[number];

/** `GET /tenants/:t/documents` — one level of the tree, or search results when `q` is set (inside `folderId` when given). */
export const documentListQuerySchema = z.object({
  section: z.enum(DOCUMENT_SECTIONS).default('documents'),
  folderId: z.uuid().optional(),
  q: z.string().trim().max(200).optional(),
  sort: z.enum(DOCUMENT_SORTS).default('name'),
});
export type DocumentListQuery = z.infer<typeof documentListQuerySchema>;

/** `GET /tenants/:t/document-folders?section=` — flat list of visible folders (tree pickers, "Ko'chirish"). */
export const documentFolderTreeQuerySchema = z.object({
  section: z.enum(DOCUMENT_SECTIONS).default('documents'),
});

const shareUserIds = z.array(z.uuid()).max(500);

export const createDocumentFolderSchema = z.object({
  section: z.enum(DOCUMENT_SECTIONS).default('documents'),
  parentId: z.uuid().nullish(),
  name: z.string().trim().min(1).max(160),
  visibility: z.enum(DOCUMENT_VISIBILITIES).default('public'),
  /** users who may see a `selected` folder (tenant members) */
  shareUserIds: shareUserIds.optional(),
});
export type CreateDocumentFolderInput = z.infer<typeof createDocumentFolderSchema>;

export const updateDocumentFolderSchema = z.object({
  name: z.string().trim().min(1).max(160).optional(),
  /** move; null = section root */
  parentId: z.uuid().nullable().optional(),
  visibility: z.enum(DOCUMENT_VISIBILITIES).optional(),
  shareUserIds: shareUserIds.optional(),
});
export type UpdateDocumentFolderInput = z.infer<typeof updateDocumentFolderSchema>;

export const createDocumentSchema = z.object({
  /** a file uploaded by the caller to this tenant */
  fileId: z.uuid(),
  folderId: z.uuid().nullish(),
  /** "HUJJAT NOMI"; defaults to the file name */
  name: z.string().trim().min(1).max(255).optional(),
  /** defaults to the folder's section (or `documents` at the root) */
  section: z.enum(DOCUMENT_SECTIONS).optional(),
  /** the upload modal starts on "Shaxsiy hujjat" */
  visibility: z.enum(DOCUMENT_VISIBILITIES).default('private'),
  shareUserIds: shareUserIds.optional(),
});
export type CreateDocumentInput = z.infer<typeof createDocumentSchema>;

export const updateDocumentSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  /** move; null = section root */
  folderId: z.uuid().nullable().optional(),
  visibility: z.enum(DOCUMENT_VISIBILITIES).optional(),
  shareUserIds: shareUserIds.optional(),
});
export type UpdateDocumentInput = z.infer<typeof updateDocumentSchema>;

export interface DocumentUserRef {
  id: string;
  fullName: string;
}

interface DocumentItemBase {
  id: string;
  section: DocumentSection;
  name: string;
  visibility: DocumentVisibility;
  /** users of a `selected` item — only for its owner and tenant admins (null for others) */
  shareUserIds: string[] | null;
  /** number of users it is shared with ("2 foydalanuvchi") */
  sharedCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: DocumentUserRef | null;
  updatedBy: DocumentUserRef | null;
  /** what the caller may do with the row (row menu) */
  canEdit: boolean;
  canDelete: boolean;
}

export interface DocumentFolderDto extends DocumentItemBase {
  parentId: string | null;
  /** visible direct sub-folders */
  folderCount: number;
  /** visible documents directly inside ("154 ta fayl") */
  documentCount: number;
}

export interface DocumentDto extends DocumentItemBase {
  folderId: string | null;
  type: DocumentType;
  file: {
    id: string;
    name: string;
    mime: string;
    size: number;
    /** `GET …/documents/:id/download` (attachment) */
    downloadUrl: string;
    /** same, served inline for the PDF/image preview */
    previewUrl: string;
  };
}

export interface DocumentListResponse {
  section: DocumentSection;
  /** the opened folder (null = root) */
  folder: DocumentFolderDto | null;
  /** root → opened folder */
  breadcrumbs: Array<{ id: string; name: string }>;
  folders: DocumentFolderDto[];
  documents: DocumentDto[];
}
